import { useEffect, useId, useRef } from 'react';
import { BrandMark } from '@/components/ui/BrandMark';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { SECTIONS, SITE_NAME } from '@/data/sections';
import { CONTACT } from '@/data/site';
import { hashForSection } from '@/hooks/useHashRoute';
import { useScrolled } from '@/hooks/useScrolled';
import type { SectionId } from '@/types';
import styles from './Header.module.css';

export interface HeaderProps {
  readonly section: SectionId;
  readonly onNavigate: (section: SectionId) => void;
  readonly isDark: boolean;
  readonly onToggleTheme: () => void;
  readonly menuOpen: boolean;
  readonly onToggleMenu: () => void;
  readonly onCloseMenu: () => void;
}

export function Header({
  section,
  onNavigate,
  isDark,
  onToggleTheme,
  menuOpen,
  onToggleMenu,
  onCloseMenu,
}: HeaderProps) {
  const menuId = useId();
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const scrolled = useScrolled();

  // Close the mobile menu on Escape and on any click outside it, and return
  // focus to the toggle so keyboard users are not dropped at the top of the page.
  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      onCloseMenu();
      toggleRef.current?.focus();
    };

    // Close on `click`, not `pointerdown`. The panel is in-flow inside the
    // sticky header, so closing it shifts the page up by the panel's height —
    // on `pointerdown` that happens between press and release, and the release
    // lands on a different element, swallowing the tap the visitor intended.
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || toggleRef.current?.contains(target)) return;
      onCloseMenu();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('click', onDocumentClick);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('click', onDocumentClick);
    };
  }, [menuOpen, onCloseMenu]);

  return (
    <>
      <div className={styles.accentBar} aria-hidden="true">
        <span />
        <span />
        <span />
      </div>

      <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
        <div className={styles.inner}>
          <button
            type="button"
            className={styles.brand}
            onClick={() => onNavigate('home')}
            aria-label={`${SITE_NAME} — الانتقال إلى الرئيسية`}
          >
            <BrandMark size={40} className={styles.brandMark} />
            <span className={styles.brandText}>
              <span className={styles.brandName}>اصنع أثرك الرقمي</span>
              <span className={styles.brandBy}>{CONTACT.author}</span>
            </span>
          </button>

          <nav className={styles.nav} aria-label="التنقل الرئيسي">
            {SECTIONS.map((item) => (
              <a
                key={item.id}
                href={hashForSection(item.id)}
                className={styles.navLink}
                aria-current={item.id === section ? 'page' : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  onNavigate(item.id);
                }}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className={styles.actions}>
            <Button
              variant="icon"
              onClick={onToggleTheme}
              aria-pressed={isDark}
              title={isDark ? 'التبديل إلى الوضع النهاري' : 'التبديل إلى الوضع الليلي'}
              aria-label={isDark ? 'التبديل إلى الوضع النهاري' : 'التبديل إلى الوضع الليلي'}
            >
              <Icon name={isDark ? 'sun' : 'moon'} size={22} />
            </Button>

            <Button
              ref={toggleRef}
              variant="icon"
              className={styles.menuToggle}
              onClick={onToggleMenu}
              aria-expanded={menuOpen}
              // Only while the panel exists: the id would otherwise dangle,
              // since the panel is not rendered when the menu is closed.
              {...(menuOpen ? { 'aria-controls': menuId } : {})}
              aria-label={menuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
            >
              <Icon name={menuOpen ? 'close' : 'menu'} size={22} />
            </Button>
          </div>
        </div>

        {menuOpen ? (
          <div className={styles.menuPanel} id={menuId} ref={menuRef}>
            <nav className={styles.menuNav} aria-label="قائمة الجوال">
              {SECTIONS.map((item) => (
                <a
                  key={item.id}
                  href={hashForSection(item.id)}
                  className={styles.menuLink}
                  aria-current={item.id === section ? 'page' : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    onNavigate(item.id);
                  }}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        ) : null}
      </header>
    </>
  );
}
